
import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight, BookOpen, Heart, Zap, Search, X, Sparkles, Users } from 'lucide-react';
import api from '../services/api';
import { Float } from '../components/Animations';
import ArchetypeCard from '../components/ArchetypeCard';
import DatabaseHero from '../components/DatabaseHero';

// Utility: format a category slug ('dominant-spectrum' or 'dominant_spectrum') into a display label
const formatCategoryLabel = (cat) => {
    if (!cat) return 'Uncategorized';
    return cat
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
};

// Color palette for dynamically generated category groups
const GROUP_COLORS = [
    { color: 'text-wine-400', bgColor: 'bg-wine-500/10', borderColor: 'border-wine-500/20' },
    { color: 'text-purple-400', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/20' },
    { color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20' },
    { color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20' },
    { color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20' },
    { color: 'text-rose-400', bgColor: 'bg-rose-500/10', borderColor: 'border-rose-500/20' },
    { color: 'text-cyan-400', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/20' },
    { color: 'text-orange-400', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/20' },
    { color: 'text-indigo-400', bgColor: 'bg-indigo-500/10', borderColor: 'border-indigo-500/20' },
    { color: 'text-pink-400', bgColor: 'bg-pink-500/10', borderColor: 'border-pink-500/20' },
];

// Icon mapping for common category keywords
const getCategoryIcon = (cat) => {
    const lower = (cat || '').toLowerCase();
    if (lower.includes('dominant') || lower.includes('nurturing')) return Zap;
    if (lower.includes('submissive') || lower.includes('service')) return Heart;
    if (lower.includes('switch')) return Sparkles;
    return BookOpen;
};


const ArchetypesPage = () => {
    const [archetypes, setArchetypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Track expanded state - first category will be auto-expanded
    const [expandedGroups, setExpandedGroups] = useState({});
    const [expandedCategories, setExpandedCategories] = useState({});
    const [searchQuery, setSearchQuery] = useState('');

    // Derive unique categories from actual data
    const dynamicCategories = useMemo(() => {
        const catMap = {};
        archetypes.forEach(a => {
            // Normalize: treat hyphens and underscores as equivalent
            const normalized = (a.category || 'uncategorized').replace(/_/g, '-');
            if (!catMap[normalized]) {
                catMap[normalized] = [];
            }
            catMap[normalized].push(a);
        });
        return Object.keys(catMap)
            .sort()
            .map((cat, idx) => ({
                id: cat,
                label: formatCategoryLabel(cat),
                items: catMap[cat],
                icon: getCategoryIcon(cat),
                ...GROUP_COLORS[idx % GROUP_COLORS.length]
            }));
    }, [archetypes]);

    // Auto-expand first category when data loads
    useEffect(() => {
        if (dynamicCategories.length > 0 && Object.keys(expandedCategories).length === 0) {
            setExpandedCategories({ [dynamicCategories[0].id]: true });
        }
    }, [dynamicCategories]);

    // Debounced search effect
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchArchetypes(searchQuery);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Initial load handled by the search effect
    // useEffect(() => {
    //     fetchArchetypes();
    // }, []);

    const fetchArchetypes = async (query = '') => {
        try {
            setLoading(true);
            const data = await api.getArchetypes(query ? { q: query } : {});
            setArchetypes(data);
        } catch (err) {
            setError('Failed to load archetypes.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
    };

    // Removed blocking loading screen - page renders immediately with inline loading states

    if (error) {
        return (
            <div className="min-h-screen bg-dark-950 flex items-center justify-center">
                <div className="text-red-400">{error}</div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-charcoal">
            {/* Page Header with Search */}
            <div className="flex-shrink-0 border-b border-wine-900/20 px-6 py-4">
                <div className="max-w-[1800px] mx-auto">
                    <div className="flex items-center gap-4">
                        {/* Search - takes most space */}
                        <div className="flex-1">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search archetypes by name, description, or category..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="input pl-12 pr-12"
                                />
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-500">
                                    <Search className="w-5 h-5" />
                                </div>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-dark-500 hover:text-dark-300"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto w-full">
                <main className="max-w-[1800px] w-full mx-auto px-6 pt-4 pb-12">
                    {/* Hero Section */}
                    {!searchQuery.trim() && (
                        <DatabaseHero
                            icon={Users}
                            label="Know Yourself"
                            title="Discover Your Sexual Archetype"
                            highlightWord="Archetype"
                            description="Explore the psychological patterns, desires, and identities that shape who you are in intimate spaces."
                            stats={[
                                { value: archetypes.length, label: 'Archetypes' },
                                { value: dynamicCategories.length, label: 'Categories' }
                            ]}
                            loading={loading}
                        />
                    )}

                    {/* Search Results */}
                    {searchQuery.trim() && (
                        <div className="mb-6">
                            <p className="text-sm text-dark-500 mb-3">
                                {archetypes.length} result{archetypes.length !== 1 ? 's' : ''} for "{searchQuery}"
                            </p>
                            {archetypes.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {archetypes.map(archetype => (
                                        <ArchetypeCard key={archetype.id} archetype={archetype} />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-dark-500">
                                    No archetypes found matching your search.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Dynamic Category Accordion */}
                    {!searchQuery.trim() && (
                        <div className="space-y-3">
                            {dynamicCategories.map((category) => {
                                const CategoryIcon = category.icon;
                                const isExpanded = expandedCategories[category.id];

                                return (
                                    <div
                                        key={category.id}
                                        className={`border overflow-hidden bg-dark-900/50 ${category.borderColor}`}
                                    >
                                        {/* Category Header */}
                                        <button
                                            onClick={() => toggleCategory(category.id)}
                                            className="w-full px-4 py-3 md:px-6 md:py-4 flex items-center justify-between hover:bg-dark-800/50 transition-colors"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 ${category.bgColor}`}>
                                                    <CategoryIcon className={`w-5 h-5 ${category.color}`} />
                                                </div>
                                                <div className="text-left">
                                                    <h2 className="text-lg font-fantasy font-semibold text-cream/90 tracking-wide">
                                                        {category.label}
                                                    </h2>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-dark-500 bg-dark-800 px-2 py-1">
                                                    {category.items.length}
                                                </span>
                                                {isExpanded ? (
                                                    <ChevronDown className="w-5 h-5 text-dark-400" />
                                                ) : (
                                                    <ChevronRight className="w-5 h-5 text-dark-400" />
                                                )}
                                            </div>
                                        </button>

                                        {/* Archetype Cards */}
                                        {isExpanded && (
                                            <div className="border-t border-dark-800 px-4 py-4 md:px-6 bg-dark-900/30">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                    {category.items.map(archetype => (
                                                        <ArchetypeCard
                                                            key={archetype.id}
                                                            archetype={archetype}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default ArchetypesPage;
